import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Phone,
  Quote,
  Star,
  Heart,
  Users,
  Utensils,
} from "lucide-react";
import {
  BUSINESS,
  FEATURED_DISHES,
  EVENTS,
  TESTIMONIALS,
  HERO_IMAGE,
  LOGO_URL,
} from "../lib/data";
import { Reveal, SectionHeading, ScriptKicker, FilipinoSun } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";

export default function Home() {
  usePageMeta({
    title: "Ateng K Sarap | Authentic Filipino Catering in San Diego",
    description:
      "Family-owned Filipino catering in San Diego. Authentic, home-style dishes for birthdays, corporate events & parties of any size. Get your free quote today!",
    path: "/",
  });
  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* ====== HERO ====== */}
      <section data-testid="hero-section" className="relative pt-10 pb-20 lg:pt-16 lg:pb-32 bg-background">
        <div className="absolute top-10 right-10 text-accent/30 hidden lg:block">
          <FilipinoSun size={140} />
        </div>
        <div className="absolute bottom-20 left-6 text-primary/15 hidden lg:block rotate-12">
          <FilipinoSun size={90} />
        </div>

        <div className="container-eds relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 relative z-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-px bg-secondary" />
              <span className="overline">San Diego · Filipino Catering</span>
            </div>
            <ScriptKicker className="!text-3xl md:!text-4xl">Mabuhay! Welcome home.</ScriptKicker>
            <h1
              data-testid="hero-headline"
              className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-medium leading-[0.95] tracking-tight text-foreground"
            >
              Taste the{" "}
              <span className="italic text-secondary relative inline-block">
                Flavors
                <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 200 14" preserveAspectRatio="none">
                  <path d="M2 8 Q 50 0 100 8 T 198 8" stroke="hsl(40 90% 60%)" strokeWidth="4" fill="none" strokeLinecap="round" />
                </svg>
              </span>
              <br />
              of <span className="italic">Home</span>.
            </h1>
            <p className="mt-7 text-lg text-muted-foreground leading-relaxed max-w-xl">
              Authentic Filipino catering in San Diego — flavorful, heartfelt cuisine
              made with love for every celebration.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid="hero-book-now-btn" className="btn-secondary">
                Book Us Now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/menu" data-testid="hero-view-menu-btn" className="btn-outline">
                View Menu
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1 text-accent">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <span className="font-semibold text-foreground">500+ Events Catered</span>
              </div>
              <div className="text-muted-foreground">
                <span className="font-script text-xl text-secondary">Made with love</span> · since day 1
              </div>
            </div>
          </div>

          {/* Right: asymmetric photo collage */}
          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] max-w-md mx-auto lg:max-w-none">
              {/* main image */}
              <motion.div
                initial={{ opacity: 0, y: 30, rotate: 0 }}
                animate={{ opacity: 1, y: 0, rotate: -3 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 polaroid"
              >
                <span className="tape" />
                <img src={HERO_IMAGE} alt="Filipino feast" fetchpriority="high" decoding="async" className="w-full h-full object-cover" />
                <div className="polaroid-caption">Family feast, served warm</div>
              </motion.div>

              {/* floating photo bottom-left */}
              <motion.div
                initial={{ opacity: 0, x: -40, rotate: 0 }}
                animate={{ opacity: 1, x: 0, rotate: 5 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
                className="hidden md:block absolute -bottom-10 -left-10 w-56 polaroid"
              >
                <img
                  src="/images/event-food.webp"
                  alt="Catering tray"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-44 object-cover"
                />
                <div className="polaroid-caption">Catering tray</div>
              </motion.div>

              {/* coral stamp top-right */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1, rotate: 12 }}
                transition={{ duration: 0.8, delay: 0.5, type: "spring" }}
                className="absolute -top-6 -right-4 lg:-right-6 w-28 h-28 rounded-full bg-secondary text-secondary-foreground flex flex-col items-center justify-center text-center shadow-xl"
              >
                <div className="font-script text-xl leading-none">Open</div>
                <div className="font-display text-lg font-bold leading-tight">for<br/>Bookings</div>
              </motion.div>

              {/* sun icon decoration */}
              <div className="hidden lg:block absolute -top-12 left-8 text-primary">
                <FilipinoSun size={64} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== MARQUEE ====== */}
      <section data-testid="marquee-strip" className="bg-primary text-primary-foreground py-5 overflow-hidden border-y-2 border-foreground">
        <div className="marquee-track">
          {[...Array(2)].map((_, j) => (
            <div key={j} className="flex items-center gap-12 shrink-0">
              {[
                "Pansit", "Belly Lechon", "Pork Shanghai", "Kare-Kare",
                "Beef Apritada", "Chop Suey", "Leche Flan", "Lumpia",
                "Adobo", "Sinigang", "Bibingka", "Ube Halaya",
              ].map((d, i) => (
                <div key={`${j}-${i}`} className="flex items-center gap-12 shrink-0">
                  <span className="font-display text-2xl lg:text-3xl italic">{d}</span>
                  <span className="text-accent text-3xl">✦</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ====== ABOUT — asymmetric ====== */}
      <section data-testid="about-section" className="py-20 lg:py-32 bg-background relative">
        <div className="container-eds grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-32 relative">
            <div className="relative">
              <img
                src="/images/catering-spread.webp"
                alt="Family catering"
                loading="lazy"
                decoding="async"
                className="w-full h-[480px] object-cover rounded-tl-[80px] rounded-br-[80px]"
              />
              <div className="absolute -bottom-8 -right-6 w-32 h-32 rounded-full bg-accent flex items-center justify-center shadow-xl rotate-12">
                <div className="text-center">
                  <div className="font-display text-3xl font-bold text-accent-foreground leading-none">15+</div>
                  <div className="text-[10px] tracking-widest uppercase text-accent-foreground/80 mt-1">Years</div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7 lg:pl-8" delay={0.15}>
            <div className="overline flex items-center gap-3">
              <span className="w-8 h-px bg-secondary" /> Our Story
            </div>
            <ScriptKicker className="block mt-3">Bayanihan in every bite</ScriptKicker>
            <h2 className="mt-2 font-display text-4xl lg:text-5xl xl:text-6xl font-medium text-foreground leading-[1.02] tracking-tight">
              From our family's <span className="italic text-primary">kitchen</span>
              <br />
              to your <span className="italic text-secondary">celebration</span>.
            </h2>
            <p className="mt-7 text-muted-foreground text-base lg:text-lg leading-relaxed">
              Ateng K Sarap was born from a love of family, food, and the celebrations
              that bring us together. Every tray we cater carries the warmth of
              Filipino hospitality — the slow simmer of an adobo, the crackle of
              lechon skin, the way pansit always finds its way to the center.
            </p>
            <p className="mt-4 text-muted-foreground text-base lg:text-lg leading-relaxed">
              We cook the way our ate, lola, and titas taught us — generously,
              joyfully, and always made with love.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4">
              {[
                { n: "500+", l: "Events Catered" },
                { n: "15+", l: "Signature Dishes" },
                { n: "100%", l: "Family Recipes" },
              ].map((s) => (
                <div key={s.n} className="bg-card p-5 rounded-2xl border border-border">
                  <div className="font-display text-3xl lg:text-4xl font-semibold text-primary">{s.n}</div>
                  <div className="text-[11px] tracking-wider uppercase text-muted-foreground mt-1">{s.l}</div>
                </div>
              ))}
            </div>

            <Link to="/about" data-testid="about-learn-more-link"
                  className="mt-10 inline-flex items-center gap-2 font-semibold text-primary hover:text-secondary transition-colors group">
              Read Our Full Story
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ====== FEATURED DISHES — asymmetric tetris ====== */}
      <section data-testid="featured-dishes-section" className="py-20 lg:py-32 bg-card relative">
        <div className="absolute top-16 left-10 text-secondary/15 hidden lg:block">
          <FilipinoSun size={100} />
        </div>
        <div className="container-eds relative">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
              <div className="lg:col-span-8">
                <div className="overline flex items-center gap-3">
                  <span className="w-8 h-px bg-secondary" /> Top Favorites
                </div>
                <ScriptKicker className="block mt-2">Signature, with stories</ScriptKicker>
                <h2 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium text-foreground leading-[0.98] tracking-tight">
                  Dishes made the way{" "}
                  <span className="italic text-primary">lola</span> taught us.
                </h2>
              </div>
              <div className="lg:col-span-4 text-muted-foreground text-base lg:text-lg leading-relaxed">
                Each plate carries a story — handed down through generations and
                served with love at every Ateng K Sarap table.
              </div>
            </div>
          </Reveal>

          <div className="mt-16 grid grid-cols-12 gap-5 lg:gap-6">
            {FEATURED_DISHES.map((d, i) => {
              const layouts = [
                "col-span-12 md:col-span-7 lg:col-span-5",
                "col-span-12 md:col-span-5 lg:col-span-4 md:translate-y-12",
                "col-span-12 md:col-span-6 lg:col-span-3",
                "col-span-12 md:col-span-6 lg:col-span-4",
                "col-span-12 md:col-span-7 lg:col-span-5 lg:translate-y-6",
                "col-span-12 md:col-span-5 lg:col-span-3",
              ];
              return (
                <Reveal key={d.name} delay={i * 0.06} className={layouts[i]}>
                  <article
                    data-testid={`dish-card-${d.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group bg-background rounded-3xl overflow-hidden border border-border h-full flex flex-col transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl"
                  >
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img
                        src={d.image}
                        alt={d.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-accent text-accent-foreground text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 font-bold rounded-full">
                        <Star className="w-3 h-3 fill-current" /> Top {String(i + 1).padStart(2, "0")}
                      </div>
                    </div>
                    <div className="p-6 lg:p-7 flex-1 flex flex-col">
                      <h3 className="font-display text-2xl lg:text-3xl font-semibold text-foreground">
                        {d.name}
                      </h3>
                      <p className="mt-1 font-script text-xl text-secondary">{d.tagline}</p>
                      <p className="mt-4 text-sm text-muted-foreground leading-relaxed flex-1">
                        {d.description}
                      </p>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="mt-20 text-center">
            <Link to="/menu" data-testid="view-full-menu-btn" className="btn-primary">
              View Full Menu <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ====== EVENTS — editorial split list ====== */}
      <section data-testid="events-section" className="py-20 lg:py-32 bg-background">
        <div className="container-eds">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
              <div className="lg:col-span-7">
                <div className="overline flex items-center gap-3">
                  <span className="w-8 h-px bg-secondary" /> Events We Cater
                </div>
                <ScriptKicker className="block mt-2">Every celebration deserves</ScriptKicker>
                <h2 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium text-foreground leading-[0.98] tracking-tight">
                  Filipino <span className="italic text-secondary">warmth</span>.
                </h2>
              </div>
              <div className="lg:col-span-5 lg:text-right">
                <Link to="/catering" className="btn-outline">All Catering Options <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </Reveal>

          <div className="mt-16 space-y-4 lg:space-y-6">
            {EVENTS.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.06}>
                <div
                  data-testid={`event-card-${e.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`group grid grid-cols-12 gap-4 lg:gap-8 items-center py-6 lg:py-8 border-b border-border ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}
                >
                  <div className={`col-span-12 md:col-span-4 lg:col-span-3 ${i % 2 === 1 ? "md:order-last" : ""}`}>
                    <div className="relative aspect-square overflow-hidden rounded-3xl">
                      <img
                        src={e.image}
                        alt={e.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                  </div>
                  <div className="col-span-3 md:col-span-2 lg:col-span-2">
                    <div className="font-display text-5xl lg:text-7xl text-accent font-medium tracking-tight">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="col-span-9 md:col-span-6 lg:col-span-7">
                    <h3 className="font-display text-3xl lg:text-5xl font-medium text-foreground group-hover:text-primary transition-colors">
                      {e.title}
                    </h3>
                    <p className="mt-3 text-muted-foreground text-base lg:text-lg max-w-2xl">{e.blurb}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ====== CULTURE — Coastal block ====== */}
      <section data-testid="culture-section" className="relative py-20 lg:py-32 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute -top-20 -right-20 text-accent/20">
          <FilipinoSun size={400} />
        </div>
        <div className="container-eds grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
          <Reveal className="lg:col-span-5">
            <div className="overline !text-accent flex items-center gap-3">
              <span className="w-8 h-px bg-accent" /> Filipino Culture
            </div>
            <ScriptKicker className="!text-accent block mt-3">Pamilya, Pagkain, Pagmamahal</ScriptKicker>
            <h2 className="mt-2 font-display text-4xl lg:text-5xl xl:text-6xl font-medium leading-[1.02] tracking-tight">
              Home. Celebration.
              <br />
              <span className="italic text-accent">Family.</span>
            </h2>
            <p className="mt-6 text-primary-foreground/85 text-base lg:text-lg leading-relaxed">
              In the Philippines, food is how we say "I love you." It's the center
              of every gathering — the boodle spread shared with hands, the long
              table set for titos and titas, the dessert that arrives last but
              somehow first into memory.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              {["Bayanihan Spirit", "Family Tradition", "Fiesta Joy", "Heartfelt Hospitality"].map((t) => (
                <span key={t} className="px-4 py-2 rounded-full border border-primary-foreground/30 text-xs tracking-[0.2em] uppercase text-primary-foreground/90">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7 grid grid-cols-12 gap-3 lg:gap-4" delay={0.15}>
            <div className="col-span-7 polaroid bg-background rotate-l1">
              <img
                src="/images/hero-food.webp"
                alt="Filipino feast"
                loading="lazy"
                decoding="async"
                className="w-full h-64 object-cover"
              />
              <div className="polaroid-caption">Feast at the table</div>
            </div>
            <div className="col-span-5 mt-12 polaroid bg-background rotate-r2">
              <img
                src="/images/event-food.webp"
                alt="Catering"
                loading="lazy"
                decoding="async"
                className="w-full h-56 object-cover"
              />
              <div className="polaroid-caption">Catering setup</div>
            </div>
            <div className="col-span-5 polaroid bg-background rotate-l2 -mt-6">
              <img
                src="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=700&q=70&fm=webp"
                alt="Celebration"
                loading="lazy"
                decoding="async"
                className="w-full h-52 object-cover"
              />
              <div className="polaroid-caption">Pamilya, kasalan</div>
            </div>
            <div className="col-span-7 polaroid bg-background rotate-r1">
              <img
                src="/images/catering-spread.webp"
                alt="Catering spread"
                loading="lazy"
                decoding="async"
                className="w-full h-56 object-cover"
              />
              <div className="polaroid-caption">Boodle spread</div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ====== TESTIMONIALS — scattered polaroids ====== */}
      <section data-testid="testimonials-section" className="py-20 lg:py-32 bg-background">
        <div className="container-eds">
          <Reveal>
            <SectionHeading
              overline="Kind Words"
              kicker="From our happy familyhood"
              title="Loved by families across San Diego."
              align="center"
              testid="testimonials-heading"
            />
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TESTIMONIALS.map((t, i) => {
              const rots = ["rotate-l1", "rotate-r1", "rotate-l2", "rotate-r2"];
              return (
                <Reveal key={t.name} delay={i * 0.08}>
                  <figure
                    data-testid={`testimonial-${i}`}
                    className={`bg-card border border-border rounded-2xl p-8 h-full flex flex-col ${rots[i]} hover:rotate-0 transition-transform duration-500 hover:shadow-xl`}
                  >
                    <Quote className="w-7 h-7 text-secondary" />
                    <blockquote className="mt-4 text-base text-foreground leading-relaxed flex-1">
                      "{t.quote}"
                    </blockquote>
                    <figcaption className="mt-6 pt-5 border-t border-border">
                      <div className="font-script text-2xl text-primary">{t.name}</div>
                      <div className="text-[11px] tracking-wider uppercase text-muted-foreground mt-0.5">{t.role}</div>
                    </figcaption>
                  </figure>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====== CTA ====== */}
      <section data-testid="cta-section" className="relative py-20 lg:py-28 bg-secondary text-secondary-foreground overflow-hidden">
        <div className="absolute -top-20 -left-20 text-accent/25"><FilipinoSun size={280} /></div>
        <div className="absolute -bottom-20 -right-20 text-primary/25 rotate-45"><FilipinoSun size={320} /></div>
        <div className="container-eds relative text-center">
          <Reveal>
            <ScriptKicker className="!text-accent text-3xl md:text-4xl block">Let's celebrate together</ScriptKicker>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium leading-[1.02] tracking-tight max-w-3xl mx-auto">
              Let us cater your next <span className="italic">celebration</span>.
            </h2>
            <p className="mt-6 text-secondary-foreground/90 max-w-xl mx-auto text-base lg:text-lg">
              Tailored to your budget and event. Orders must be placed one week
              prior to your event.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="cta-reserve-btn"
                    className="inline-flex items-center gap-2 bg-background text-foreground hover:bg-accent hover:text-accent-foreground px-7 py-3.5 text-[12px] font-bold tracking-[0.12em] uppercase rounded-full border-2 border-background hover:border-accent transition-all">
                Reserve Your Event <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={BUSINESS.phoneLink} data-testid="cta-call-btn" className="btn-outline-light">
                <Phone className="w-4 h-4" /> {BUSINESS.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
